/**
 * Command Metadata
 * Auto-generated by generate-command-metadata.js from Grammar JSON files
 */

export type CommandMetadataEntry = {
    doc: string;
    parameterNames: Array<string>;
    count: number;
    outputParamIndices?: Array<number>;
    literalParamIndices?: Array<number>;
    optionalArguments?: Array<string>;
};

export const COMMAND_METADATA: Record<string, CommandMetadataEntry> = {
    abort: {
        doc: 'Abort the current script and jump to on_finished: block',
        parameterNames: [],
        count: 0,
    },
    add_var: {
        doc: 'Add values to a variable.',
        parameterNames: ['var', 'value'],
        count: 2,
    },
    alter_vartype: {
        doc: 'Change variable type.',
        parameterNames: ['var', 'vartype'],
        count: 2,
        outputParamIndices: [0],
    },
    array: {
        doc: 'Declare an array. This will allocate the required memory for the array. All arrays must be declared before use.',
        parameterNames: ['name', 'size'],
        count: 2,
        outputParamIndices: [0],
    },
    array_get: {
        doc: 'Get value from array.',
        parameterNames: ['array', 'index', 'output'],
        count: 3,
        outputParamIndices: [2],
    },
    array_set: {
        doc: 'Set value in array.',
        parameterNames: ['array', 'index', 'value'],
        count: 3,
    },
    await_int: {
        doc: 'Pause until the next configured interval',
        parameterNames: [],
        count: 0,
    },
    battery_perc: {
        doc: "Read the battery's charge as a percentage",
        parameterNames: ['measured_percentage'],
        count: 1,
        outputParamIndices: [0],
    },
    beep: {
        doc: 'Produce a beep sound.',
        parameterNames: ['tone', 'volume', 'duration'],
        count: 3,
        literalParamIndices: [0, 1],
    },
    bit_and_var: {
        doc: 'Bitwise AND operation.',
        parameterNames: ['var', 'value'],
        count: 2,
    },
    bit_inv_var: {
        doc: 'Bitwise inversion (NOT).',
        parameterNames: ['var'],
        count: 1,
    },
    bit_lsl_var: {
        doc: 'Bitwise left shift.',
        parameterNames: ['var', 'shift'],
        count: 2,
    },
    bit_lsr_var: {
        doc: 'Bitwise right shift.',
        parameterNames: ['var', 'shift'],
        count: 2,
    },
    bit_or_var: {
        doc: 'Bitwise OR operation.',
        parameterNames: ['var', 'value'],
        count: 2,
    },
    bit_xor_var: {
        doc: 'Bitwise XOR operation.',
        parameterNames: ['var', 'value'],
        count: 2,
    },
    breakloop: {
        doc: 'Break out of the current loop.',
        parameterNames: [],
        count: 0,
    },
    cell_off: {
        doc: 'Turn off the electrochemical cell (disable cell)',
        parameterNames: [],
        count: 0,
    },
    cell_on: {
        doc: 'Turn on the electrochemical cell (enable cell and start regulation)',
        parameterNames: [],
        count: 0,
    },
    copy_var: {
        doc: 'Copy a variable (value and metadata).',
        parameterNames: ['source', 'dest'],
        count: 2,
        outputParamIndices: [1],
    },
    display_btns: {
        doc: 'Show one or two buttons on the display, then immediately update the display and wait for the user to press one.',
        parameterNames: ['pressed_button_id', 'left_text', 'right_text'],
        count: 3,
        outputParamIndices: [0],
    },
    display_clear: {
        doc: "Remove all elements from the display. This won't be reflected on-screen until it is next drawn",
        parameterNames: [],
        count: 0,
    },
    display_draw: {
        doc: 'Immediately prompt the display to be updated.',
        parameterNames: [],
        count: 0,
    },
    display_icon: {
        doc: 'Add an icon on the display, to be shown the next time the display is drawn.',
        parameterNames: ['icon_type'],
        count: 1,
    },
    display_inp_num: {
        doc: 'Prompt the user for a numerical value, and wait until one is provided.',
        parameterNames: ['prompt', 'value', 'int_or_float'],
        count: 3,
        outputParamIndices: [1],
    },
    display_keyboard: {
        doc: 'Get a line of text entered by the user and record it to the script output.',
        parameterNames: ['text'],
        count: 1,
    },
    display_progress: {
        doc: 'Add a progress bar on the display, to be shown the next time the display is drawn.',
        parameterNames: ['progress_value'],
        count: 1,
    },
    display_scroll_add: {
        doc: 'Add an entry to the scroll list on the display, to be shown using display_scroll_get.',
        parameterNames: ['text'],
        count: 1,
    },
    display_scroll_get: {
        doc: 'Show the scroll items (added by display_scroll_add) to the user, and wait for a choice to be made.',
        parameterNames: ['prompt', 'chosen'],
        count: 2,
        outputParamIndices: [1],
    },
    display_text: {
        doc: 'Show text on the display.',
        parameterNames: ['text', 'size'],
        count: 2,
    },
    div_var: {
        doc: 'Divide variables.',
        parameterNames: ['var', 'value'],
        count: 2,
    },
    else: {
        doc: 'Part of conditional statement block. Used between `if` and `endif` to execute code when no previous conditions were true.',
        parameterNames: [],
        count: 0,
    },
    elseif: {
        doc: 'Part of conditional statement block. Used between `if` and `endif` to test additional conditions.',
        parameterNames: [],
        count: 0,
    },
    endif: {
        doc: 'Terminates an `if` conditional statement block. Every `if` must have a matching `endif`.',
        parameterNames: [],
        count: 0,
    },
    endloop: {
        doc: 'Signal the end of a loop. This command is used to end a `loop` command or any of the measurement loop commands.',
        parameterNames: [],
        count: 0,
    },
    file_close: {
        doc: 'Close the currently open file.',
        parameterNames: [],
        count: 0,
    },
    file_open: {
        doc: 'Open a file.',
        parameterNames: ['filename', 'mode'],
        count: 2,
        literalParamIndices: [0, 1],
    },
    float_to_int: {
        doc: 'Convert float to integer (truncates).',
        parameterNames: ['var'],
        count: 1,
    },
    get_gpio: {
        doc: 'Read GPIO pin state.',
        parameterNames: ['values'],
        count: 1,
        outputParamIndices: [0],
    },
    get_gpio_msk: {
        doc: 'Read GPIO pins with mask.',
        parameterNames: ['mask', 'values'],
        count: 2,
        outputParamIndices: [1],
    },
    get_progress: {
        doc: 'Read the progress through the current measurement, from 0 to 100. It is an error to use this outside a measurement loop.',
        parameterNames: ['measured_percentage'],
        count: 1,
        outputParamIndices: [0],
    },
    get_time: {
        doc: 'Get time since device startup.',
        parameterNames: ['time'],
        count: 1,
        outputParamIndices: [0],
    },
    hibernate: {
        doc: 'Enter low-power hibernation mode.',
        parameterNames: ['wake_source', 'wake_time'],
        count: 2,
        literalParamIndices: [0],
    },
    i2c_config: {
        doc: 'Setup I²C configuration. This is required before using any other I²C command from MethodSCRIPT. The I²C interface supported by MethodSCRIPT always works as master. Multi-master mode is currently not supported.',
        parameterNames: ['clock_speed', 'address_mode'],
        count: 2,
    },
    i2c_read: {
        doc: 'Read one or more bytes from an I²C target device. This also generates the I²C start and stop conditions. If a NACK (Not Acknowledge) was received from the target device, the user should handle this and reset the ACK status variable.',
        parameterNames: ['device_addr', 'data', 'count', 'ack_status'],
        count: 4,
        outputParamIndices: [1],
    },
    i2c_read_byte: {
        doc: 'Receive one byte from an I²C target device. This also generates the I²C start and stop conditions. If a NACK (Not Acknowledge) was received from the target device, the user should handle this and reset the ACK status variable.',
        parameterNames: ['device_addr', 'data', 'ack_status'],
        count: 3,
        outputParamIndices: [1],
    },
    i2c_write: {
        doc: 'Write one or more bytes to an I²C target device. This also generates the I²C start and stop conditions. If a NACK (Not Acknowledge) was received from the target device, the user should handle this and reset the ACK status variable.',
        parameterNames: ['device_addr', 'data', 'count', 'ack_status'],
        count: 4,
    },
    i2c_write_byte: {
        doc: 'Transmit one byte to an I²C target device. This also generates the I²C start and stop conditions. If a NACK (Not Acknowledge) was received from the target device, the user should handle this and reset the ACK status variable.',
        parameterNames: ['device_addr', 'data', 'ack_status'],
        count: 3,
    },
    i2c_write_read: {
        doc: 'Write to and read from an I²C target device. This also generates the I²C start and stop conditions. In contrast with `i2c_read` and `i2c_write`, this command does not generate a STOP condition between writing and reading.',
        parameterNames: ['device_addr', 'tx_data', 'tx_count', 'rx_data', 'rx_count', 'ack_status'],
        count: 6,
        outputParamIndices: [3],
    },
    if: {
        doc: 'Conditional statements allow the conditional execution of commands. Every `if` statement must be terminated by an `endif` statement.',
        parameterNames: [],
        count: 0,
    },
    int_to_float: {
        doc: 'Convert integer to float.',
        parameterNames: ['var'],
        count: 1,
    },
    linear_fit: {
        doc: 'Perform a linear least squares regression on a set of data.',
        parameterNames: ['x_datapoints', 'y_datapoints', 'slope', 'intercept'],
        count: 4,
        outputParamIndices: [2, 3],
    },
    log_var: {
        doc: 'Take the natural logarithm of a variable.',
        parameterNames: ['arg1'],
        count: 1,
    },
    mean: {
        doc: 'Take the mean of an array of data.',
        parameterNames: ['data', 'mean'],
        count: 2,
        outputParamIndices: [1],
    },
    meas: {
        doc: 'Measure a data point of the specified type and store the result as a variable. The data point will be averaged for the specified amount of time at the maximum available sampling rate.',
        parameterNames: ['duration', 'output', 'vartype'],
        count: 3,
        outputParamIndices: [1],
        optionalArguments: ['add_meas'],
    },
    meas_fast_ca: {
        doc: 'Fast chronoamperometry measurement. Stores all results in arrays for high-speed data acquisition.',
        parameterNames: ['set_potential', 'measured_currents', 'points_count', 'dc_potential', 'interval_time', 'run_time'],
        count: 6,
        outputParamIndices: [0, 1, 2],
        optionalArguments: ['add_meas'],
    },
    meas_fast_cv: {
        doc: 'Fast cyclic voltammetry measurement. Stores all results in arrays for high-speed data acquisition.',
        parameterNames: [
            'set_potentials',
            'measured_currents',
            'points_count',
            'begin_potential',
            'vertex1_potential',
            'vertex2_potential',
            'step_potential',
            'scan_rate',
        ],
        count: 8,
        outputParamIndices: [0, 1, 2],
        optionalArguments: ['add_meas', 'nscans', 'nscans_avg', 'nscans_equil'],
    },
    meas_loop_acv: {
        doc: 'AC Voltammetry measurement loop.',
        parameterNames: [
            'dc_pot',
            'dc_cur',
            'ac_pot',
            'ac_cur',
            'z_real',
            'z_imag',
            'begin_potential',
            'end_potential',
            'step_potential',
            'scan_rate',
            'amplitude',
            'frequency',
        ],
        count: 12,
        outputParamIndices: [0, 1, 2, 3, 4, 5],
        optionalArguments: ['time'],
    },
    meas_loop_ca: {
        doc: 'Chronoamperometry measurement loop. Applies a constant potential and measures current over time.',
        parameterNames: ['set_potential', 'measured_current', 'dc_potential', 'interval_time', 'run_time'],
        count: 5,
        outputParamIndices: [0, 1],
        optionalArguments: ['add_meas', 'poly_we', 'time'],
    },
    meas_loop_ca_alt_mux: {
        doc: 'Chronoamperometry with multiplexer. Same as meas_loop_ca but with multiplexer support.',
        parameterNames: ['set_potential', 'measured_currents', 'dc_potential', 'interval_time', 'run_time', 'first_mux_channel', 'last_mux_channel'],
        count: 7,
        outputParamIndices: [0, 1],
        optionalArguments: ['add_meas', 'time'],
    },
    meas_loop_cp: {
        doc: 'Chronopotentiometry measurement loop. Applies constant current and measures potential over time.',
        parameterNames: ['measured_potential', 'set_current', 'dc_current', 'interval_time', 'run_time'],
        count: 5,
        outputParamIndices: [0, 1],
        optionalArguments: ['add_meas', 'poly_we', 'time'],
    },
    meas_loop_cp_alt_mux: {
        doc: 'Chronopotentiometry with multiplexer. Same as meas_loop_cp but with multiplexer support.',
        parameterNames: ['measured_potentials', 'set_current', 'dc_current', 'interval_time', 'run_time', 'first_channel', 'last_channel'],
        count: 7,
        outputParamIndices: [0, 1],
        optionalArguments: ['add_meas', 'time'],
    },
    meas_loop_cv: {
        doc: 'Cyclic voltammetry measurement loop. Sweeps potential between vertices and measures current.',
        parameterNames: ['set_potential', 'measured_current', 'begin_potential', 'vertex1_potential', 'vertex2_potential', 'step_potential', 'scan_rate'],
        count: 7,
        outputParamIndices: [0, 1],
        optionalArguments: ['add_meas', 'nscans', 'nscans_avg', 'nscans_equil', 'time'],
    },
    meas_loop_dpv: {
        doc: 'Differential Pulse Voltammetry measurement loop.',
        parameterNames: [
            'set_potential',
            'measured_current',
            'begin_potential',
            'end_potential',
            'step_potential',
            'pulse_potential',
            'pulse_time',
            'scan_rate',
        ],
        count: 8,
        outputParamIndices: [0, 1],
        optionalArguments: ['add_meas', 'time'],
    },
    meas_loop_eis: {
        doc: 'Electrochemical Impedance Spectroscopy measurement loop.',
        parameterNames: ['frequency', 'z_real', 'z_imag', 'amplitude', 'start_freq', 'end_freq', 'num_points', 'dc_potential'],
        count: 8,
        outputParamIndices: [0, 1, 2],
        optionalArguments: ['eis_tdd', 'eis_opt', 'eis_acdc', 'time'],
    },
    meas_loop_eis_dual: {
        doc: 'Dual-channel Electrochemical Impedance Spectroscopy measurement loop.',
        parameterNames: [
            'mode',
            'frequency',
            'z_real',
            'z_imag',
            'second_z_real',
            'second_z_imag',
            'amplitude',
            'start_freq',
            'end_freq',
            'num_points',
            'dc_potential',
        ],
        count: 11,
        outputParamIndices: [1, 2, 3, 4, 5],
        literalParamIndices: [0],
        optionalArguments: ['eis_dual_tdd', 'eis_dual_acdc', 'eis_opt', 'time'],
    },
    meas_loop_geis: {
        doc: 'Galvanostatic Electrochemical Impedance Spectroscopy measurement loop.',
        parameterNames: ['frequency', 'z_real', 'z_imag', 'amplitude', 'start_freq', 'end_freq', 'num_points', 'dc_current'],
        count: 8,
        outputParamIndices: [0, 1, 2],
        optionalArguments: ['eis_tdd', 'eis_opt', 'eis_acdc', 'time'],
    },
    meas_loop_lsp: {
        doc: 'Linear Sweep Potentiometry measurement loop. Sweeps current linearly and measures potential.',
        parameterNames: ['measured_potential', 'current_setpoint', 'begin_current', 'end_current', 'step_current', 'scan_rate'],
        count: 6,
        outputParamIndices: [0, 1],
        optionalArguments: ['add_meas', 'time'],
    },
    meas_loop_lsv: {
        doc: 'Linear Sweep Voltammetry measurement loop. Sweeps potential linearly and measures current.',
        parameterNames: ['set_potential', 'measured_current', 'begin_potential', 'end_potential', 'step_potential', 'scan_rate'],
        count: 6,
        outputParamIndices: [0, 1],
        optionalArguments: ['poly_we', 'add_meas', 'time'],
    },
    meas_loop_npv: {
        doc: 'Normal Pulse Voltammetry measurement loop.',
        parameterNames: ['set_potential', 'measured_current', 'begin_potential', 'end_potential', 'step_potential', 'pulse_time', 'scan_rate'],
        count: 7,
        outputParamIndices: [0, 1],
        optionalArguments: ['poly_we', 'add_meas', 'time'],
    },
    meas_loop_ocp: {
        doc: 'Open Circuit Potentiometry measurement loop. Measures potential without applying current.',
        parameterNames: ['measured_potential', 'interval_time', 'run_time'],
        count: 3,
        outputParamIndices: [0],
        optionalArguments: ['add_meas', 'time'],
    },
    meas_loop_ocp_alt_mux: {
        doc: 'Open Circuit Potentiometry with multiplexer. Same as meas_loop_ocp but with multiplexer support.',
        parameterNames: ['measured_potentials', 'interval_time', 'run_time', 'first_channel', 'last_channel'],
        count: 5,
        outputParamIndices: [0],
        optionalArguments: ['add_meas', 'time'],
    },
    meas_loop_pad: {
        doc: 'Pulsed Amperometric Detection measurement loop.',
        parameterNames: ['set_potential', 'measured_current', 'dc_potential', 'pulse_potential', 'pulse_time', 'interval_time', 'run_time', 'mode'],
        count: 8,
        outputParamIndices: [0, 1],
        literalParamIndices: [7],
        optionalArguments: ['poly_we', 'add_meas', 'time'],
    },
    meas_loop_swv: {
        doc: 'Square Wave Voltammetry measurement loop.',
        parameterNames: [
            'set_potential',
            'measured_current',
            'forward_current',
            'reverse_current',
            'begin_potential',
            'end_potential',
            'step_potential',
            'amplitude',
            'frequency',
        ],
        count: 9,
        outputParamIndices: [0, 1, 2, 3],
    },
    meas_ms_eis: {
        doc: 'Multi-sine Electrochemical Impedance Spectroscopy measurement. Measures impedance at multiple frequencies simultaneously.',
        parameterNames: ['frequencies', 'z_real', 'z_imag', 'amplitude', 'base_freq', 'dc_potential', 'preset'],
        count: 7,
        outputParamIndices: [0, 1, 2],
        optionalArguments: ['eis_tdd', 'eis_opt', 'ms_eis_acdc'],
    },
    meas_scp: {
        doc: 'Perform a Stripping Chronopotentiometry (SCP) measurement. This command performs Stripping Chronopotentiometry also known as Potentiometric Stripping Analysis (PSA).',
        parameterNames: [
            'derivative_bins',
            'bin_count',
            'bins_start_potential',
            'bins_end_potential',
            'set_current',
            'stripping_current',
            'end_potential',
            'run_time',
        ],
        count: 8,
        outputParamIndices: [0, 1, 2, 3, 4],
    },
    mod_var: {
        doc: 'Modulo operation.',
        parameterNames: ['var', 'value'],
        count: 2,
    },
    mul_var: {
        doc: 'Multiply values.',
        parameterNames: ['var', 'value'],
        count: 2,
    },
    mux_config: {
        doc: 'Configure multiplexer.',
        parameterNames: ['mux_type', 'config'],
        count: 2,
        literalParamIndices: [1],
    },
    mux_get_channel_count: {
        doc: 'Get multiplexer channel count.',
        parameterNames: ['count'],
        count: 1,
        outputParamIndices: [0],
    },
    mux_set_channel: {
        doc: 'Set active multiplexer channel.',
        parameterNames: ['channel'],
        count: 1,
    },
    notify_led: {
        doc: 'Control notification LED.',
        parameterNames: ['mode'],
        count: 1,
        literalParamIndices: [0],
    },
    pck_add: {
        doc: 'Add variable or literal to current data packet.',
        parameterNames: ['value'],
        count: 1,
        optionalArguments: ['meta_msk'],
    },
    pck_end: {
        doc: 'Transmit the completed data packet',
        parameterNames: [],
        count: 0,
    },
    pck_start: {
        doc: 'Start a new measurement data packet. Must be closed with pck_end',
        parameterNames: [],
        count: 0,
        optionalArguments: ['meta_msk'],
    },
    peak_detect: {
        doc: 'Detect peaks in data array.',
        parameterNames: ['input', 'indices', 'heights', 'direction', 'threshold'],
        count: 5,
        outputParamIndices: [1, 2],
        optionalArguments: ['window'],
    },
    pow_var: {
        doc: 'Raise variable to a power (var = var ^ exponent).',
        parameterNames: ['arg1', 'arg2'],
        count: 2,
    },
    qr_scan: {
        doc: 'Trigger the QR code scanner. The scanner recognises QR codes in a specific format containing values that can be parsed and stored in an array.',
        parameterNames: ['output_array', 'output_count'],
        count: 2,
        outputParamIndices: [0, 1],
    },
    rtc_get: {
        doc: 'Get real-time clock value.',
        parameterNames: ['year', 'month', 'day', 'hour', 'minute', 'second'],
        count: 6,
        outputParamIndices: [0, 1, 2, 3, 4, 5],
    },
    send_string: {
        doc: 'Send arbitrary string to output.',
        parameterNames: ['text'],
        count: 1,
    },
    set_acquisition_frac: {
        doc: 'Set acquisition fraction.',
        parameterNames: ['fraction'],
        count: 1,
    },
    set_acquisition_frac_autoadjust: {
        doc: 'Enable auto-adjustment of acquisition fraction',
        parameterNames: ['frequency'],
        count: 1,
    },
    set_autoranging: {
        doc: 'Configure autoranging for measurement loops.',
        parameterNames: ['vartype', 'min_value', 'max_value'],
        count: 3,
    },
    set_bipot_mode: {
        doc: 'Set bipotentiostat mode.',
        parameterNames: ['mode'],
        count: 1,
        literalParamIndices: [0],
    },
    set_bipot_potential: {
        doc: 'Set potential or offset for second WE.',
        parameterNames: ['potential'],
        count: 1,
    },
    set_channel_sync: {
        doc: 'Enable/disable measurement synchronization between channels.',
        parameterNames: ['enable'],
        count: 1,
        literalParamIndices: [0],
    },
    set_cr: {
        doc: '**DEPRECATED:** Use set_range or set_range_minmax instead. Set the current range for the given maximum current.',
        parameterNames: ['max_i'],
        count: 1,
    },
    set_e: {
        doc: 'Set electrode potential (apply DC potential to working electrode).',
        parameterNames: ['potential'],
        count: 1,
    },
    set_e_aux: {
        doc: 'Set voltage on auxiliary DAC.',
        parameterNames: ['voltage'],
        count: 1,
    },
    set_gpio: {
        doc: 'Set output values for all GPIO pins.',
        parameterNames: ['values'],
        count: 1,
    },
    set_gpio_cfg: {
        doc: 'Configure GPIO pins.',
        parameterNames: ['pin_mask', 'mode'],
        count: 2,
        literalParamIndices: [1],
    },
    set_gpio_msk: {
        doc: 'Write to masked subset of GPIO pins.',
        parameterNames: ['mask', 'values'],
        count: 2,
    },
    set_gpio_pullup: {
        doc: 'Enable/disable pull-up resistors on GPIO pins.',
        parameterNames: ['pin_mask', 'pullup'],
        count: 2,
    },
    set_i: {
        doc: 'Set current (apply DC current in galvanostatic mode).',
        parameterNames: ['current'],
        count: 1,
    },
    set_int: {
        doc: 'Configure interval for await_int.',
        parameterNames: ['interval'],
        count: 1,
    },
    set_ir_comp: {
        doc: 'Set resistance for iR compensation.',
        parameterNames: ['resistance'],
        count: 1,
    },
    set_max_bandwidth: {
        doc: 'Set maximum bandwidth of measurement signal.',
        parameterNames: ['bandwidth'],
        count: 1,
        optionalArguments: ['filter_type'],
    },
    set_pgstat_chan: {
        doc: 'Select active PGStat channel.',
        parameterNames: ['channel'],
        count: 1,
        literalParamIndices: [0],
    },
    set_pgstat_mode: {
        doc: 'Set hardware configuration mode.',
        parameterNames: ['mode'],
        count: 1,
        literalParamIndices: [0],
    },
    set_poly_we_mode: {
        doc: 'Select mode of additional WE (legacy).',
        parameterNames: ['mode'],
        count: 1,
        literalParamIndices: [0],
    },
    set_pot_range: {
        doc: '**DEPRECATED:** Use set_range or set_range_minmax instead. Set the expected potential range for measurements.',
        parameterNames: ['potential_1', 'potential_2'],
        count: 2,
    },
    set_range: {
        doc: 'Set expected measurement range.',
        parameterNames: ['vartype', 'max_value'],
        count: 2,
    },
    set_range_minmax: {
        doc: 'Set expected min/max measurement range.',
        parameterNames: ['vartype', 'min_value', 'max_value'],
        count: 3,
    },
    set_scan_dir: {
        doc: 'Set scan direction.',
        parameterNames: ['direction'],
        count: 1,
        literalParamIndices: [0],
    },
    set_script_output: {
        doc: 'Configure script output destination.',
        parameterNames: ['mode'],
        count: 1,
        literalParamIndices: [0],
    },
    smooth: {
        doc: 'Apply Savitzky-Golay smoothing to data.',
        parameterNames: ['input', 'output', 'smoothness'],
        count: 3,
        outputParamIndices: [1],
    },
    store_str: {
        doc: 'Store a string in a string variable.',
        parameterNames: ['destination', 'source'],
        count: 2,
        literalParamIndices: [1],
    },
    store_var: {
        doc: 'Store literal value and type in variable.',
        parameterNames: ['var', 'value', 'vartype'],
        count: 3,
        outputParamIndices: [0],
    },
    str: {
        doc: 'Create a string variable from value',
        parameterNames: ['name'],
        count: 1,
        literalParamIndices: [0],
    },
    sub_var: {
        doc: 'Subtract values.',
        parameterNames: ['var', 'value'],
        count: 2,
    },
    subarray: {
        doc: 'Extract a portion of an array',
        parameterNames: ['array_variable', 'source_array', 'array_index', 'length'],
        count: 4,
        outputParamIndices: [0],
    },
    timer_get: {
        doc: 'Get elapsed time since last timer_start.',
        parameterNames: ['time'],
        count: 1,
        outputParamIndices: [0],
    },
    timer_start: {
        doc: 'Start or reset high-resolution timer',
        parameterNames: [],
        count: 0,
    },
    trim_enable: {
        doc: 'Enable or disable trimming for a given VarType. Enabling trimming allows use of lower ranges by removing DC offset.',
        parameterNames: ['variable_type', 'enable'],
        count: 2,
    },
    var: {
        doc: 'Declare a new variable',
        parameterNames: ['name'],
        count: 1,
    },
    wait: {
        doc: 'Pause script execution.',
        parameterNames: ['time'],
        count: 1,
    },
};

export const COMMAND_PARAMETER_COUNTS: Record<string, number> = Object.fromEntries(Object.entries(COMMAND_METADATA).map(([cmd, meta]) => [cmd, meta.count]));

export const COMMAND_PARAMETER_NAMES: Record<string, Array<string>> = Object.fromEntries(
    Object.entries(COMMAND_METADATA).map(([cmd, meta]) => [cmd, meta.parameterNames]),
);

/**
 * Optional arguments metadata for commands
 * Maps command name to array of optional argument names
 */
export const COMMAND_OPTIONAL_ARGUMENTS: Record<string, Array<string>> = Object.fromEntries(
    Object.entries(COMMAND_METADATA)
        .filter(([, meta]) => meta.optionalArguments && meta.optionalArguments.length > 0)
        .map(([cmd, meta]) => [cmd, meta.optionalArguments ?? []]),
);

/**
 * Get optional arguments for a command
 * Returns empty array if command doesn't support optional arguments
 */
export function getOptionalArguments(commandName: string): Array<string> {
    return COMMAND_OPTIONAL_ARGUMENTS[commandName] || [];
}

/**
 * Output parameter indices (0-indexed positions) for commands
 * These identify which parameters receive output from the command (marked as [out]).
 * These parameters should NOT show completions because they must be pre-declared variables.
 *
 * Note: All variables must be declared before use - there is no auto-declaration.
 * Output parameters [out] must already exist when the command is called.
 */
export const COMMAND_OUTPUT_PARAM_INDICES: Record<string, Array<number>> = Object.fromEntries(
    Object.entries(COMMAND_METADATA)
        .filter(([, meta]) => meta.outputParamIndices && meta.outputParamIndices.length > 0)
        .map(([cmd, meta]) => [cmd, meta.outputParamIndices ?? []]),
);

/**
 * Check if a parameter index is an output parameter for a command
 * Output parameters should NOT show completions
 */
export function isOutputParameter(commandName: string, parameterIndex: number): boolean {
    const outputParams = COMMAND_OUTPUT_PARAM_INDICES[commandName];
    return outputParams ? outputParams.includes(parameterIndex) : false;
}

/**
 * Literal parameter indices (0-indexed positions) for commands
 * These identify which parameters accept only literal values (e.g., LiteralUInt8/16/32, LiteralInt).
 * Integer types are NOT variable types in MethodSCRIPT — they are always literal values.
 * These parameters should NOT show variable completions.
 */
export const COMMAND_LITERAL_PARAM_INDICES: Record<string, Array<number>> = Object.fromEntries(
    Object.entries(COMMAND_METADATA)
        .filter(([, meta]) => meta.literalParamIndices && meta.literalParamIndices.length > 0)
        .map(([cmd, meta]) => [cmd, meta.literalParamIndices ?? []]),
);

/**
 * Check if a parameter index is a literal-only parameter for a command
 * Literal parameters should NOT show variable completions — only literal values or no completions
 */
export function isLiteralParameter(commandName: string, parameterIndex: number): boolean {
    const literalParams = COMMAND_LITERAL_PARAM_INDICES[commandName];
    return literalParams ? literalParams.includes(parameterIndex) : false;
}
